<script setup lang="ts">
  import {useI18n} from 'vue-i18n';
  import {computed, watch, ref} from 'vue';
  import useCpBem from '~/composables/useCpBem';
  import type {IQuestion} from '~/utils/fixtures/QnA.fixtures';
  import {questions} from '~/utils/fixtures/QnA.fixtures';
  import CpAvatar from '~/components/cp/CpAvatar/CpAvatar.vue';
  import CpPillPdp from '~/components/cp/CpPill/CpPillPdp/CpPillPdp.vue';
  import CpText from '~/components/cp/CpText/CpText.vue';
  import CpButtonIcon from '~/components/cp/CpButtonIcon/CpButtonIcon.vue';
  import CpDivider from '~/components/cp/CpDivider/CpDivider.vue';
  import CpImage from '~/components/cp/CpImage/CpImage.vue';
  import CpCardHeader from '~/components/cp/CpCardHeader/CpCardHeader.vue';
  import CpThumbFeedback from '~/components/cp/CpThumb/CpThumbFeedback/CpThumbFeedback.vue';
  import CpModalReportUcg from '~/components/cp/CpModal/CpModalReportUcg/CpModalReportUcg.vue';
  import CpModalActions from '~/components/cp/CpModal/CpModalActions/CpModalActions.vue';
  import CpModalDialog from '~/components/cp/CpModal/CpModalDialog/CpModalDialog.vue';
  import CpTextParser from '../CpTextParser/CpTextParser.vue';
  import CpNuxtLink from '~/components/cp/CpNuxtLink/CpNuxtLink.vue';
  import {useVoteAnswer} from '~/composables/queries/useQnA';

  const props = defineProps<{
    pdp?: boolean,
    allAnswers?: boolean,
    isLogged?: boolean
    question: IQuestion,
  }>();

  const emit = defineEmits<{
    (e:'report-question', id: string): void
    (e:'report-answer', id: string): void
  }>();

  const links = computed (() => {
      return questions[0].answers[0].attachments.urls.length > 0;
  });
  const video = computed (() => {
      return questions[0].answers[0].attachments.video.length > 0;
  });
  const hasImages = computed (() => {
      return questions[0].answers[0].attachments.image.length > 0;
  });

  const answers = computed(() => {
    if (props.allAnswers) {
      return props.question.answers;
    } else {
      return props.question.answers.length > 0 ? [props.question.answers[0]] : [];
    }
  });

  function emitEvent(event: string, id: string) {
    if (event === 'report-answer') {
      emit('report-answer', id);
    } else {
      emit('report-question', id);
    }
  }

  const {t} = useI18n();
  const {bm, e, em} = useCpBem('cp-card-qna-answer');

  const showModalReport = ref<boolean>(false);
  const showModalReportForm = ref<boolean>(false);
  const success = ref<boolean>(false);
  const reportReason = ref<string>('');
  const loading = ref<boolean>(false);
  const showModalRequiredLogin = ref<boolean>(false);
  const reportType = ref<'question'|'answer'|'review'>('question');
  const dataToReport = ref();
  const redirectTo = ref<'report'>('report');

  const modalReport = (type: 'question'|'answer', id: string) => {
    reportType.value = type;
    dataToReport.value = getDataToReport(id);
    showModalReport.value = true;
  };

  const getDataToReport = (id: string) => {
    let dataToReport;
    if (reportType.value === 'answer') {
        props.question.answers.forEach((answer) => {
          if (answer.id === id) {
            dataToReport = answer;
          }
        });
    } else {
      dataToReport = props.question;
    }
    return dataToReport;
  };

  const report = () => {
    if (props.isLogged) {
      modalReportForm();
    } else {
      showModalReport.value = false;
      showModalRequiredLogin.value = true;
    }
  };

  const modalReportForm = () => {
    showModalReport.value = false;
    showModalReportForm.value = true;
  };


  const sendReport = async () => {
    success.value = false;
    loading.value = true;
    try {
      await useReportContent({
        type: reportType.value,
        data: dataToReport.value,
        reason: reportReason.value,
        user: '',
      });
    } catch {}

    success.value = true;
    loading.value = false;
    dataToReport.value = null;
  };

  const showModalLogin = () => {
    showModalRequiredLogin.value = false;
    redirectTo.value = 'report';
    try {
      useEvent('show-login-modal', {show: true});
    } catch {}
  };

  watch(props, () => {
    if (props.isLogged && dataToReport.value) {
      if (redirectTo.value === 'report') {
        showModalReportForm.value = true;
      }
    }
  });

  const vote = async (answerId: string, vote: '1' | '0') => {
    await useVoteAnswer({answerId, vote});
  };
</script>

<template>
    <div :class="bm( {'full-card': allAnswers || pdp})" data-testid="cp-card-qna-answer">
        <CpNuxtLink
            data-testid="cp-card-qna-answer-link"
            :to="{ name: 'details-all-answers', params: { id: question.articleId, questionId: question.id } }"
        >
            <CpCardHeader
                v-if="pdp"
                :class="em('card-header', {'no-answers': answers.length === 0 })"
                kind="pdp"
                :image="question.userPic"
                :question="question.body"
                :user-name="question.username"
                :question-date="question.date"
                :quantity="question.answerCount.toString()"
                @actions-click="emitEvent('report-question', question.id); modalReport('question', question.id)"
            />
        </CpNuxtLink>
        <div
            v-for="(item, index) in answers"
            :key="item.id"
            :class="em('main-card', {'last-card': index === answers.length - 1 || (answers.length===0 && index===0)})"
        >
            <div>
                <CpAvatar size="small" :image="item.userPic" :alt="item.userName" />
            </div>
            <div :class="e('right-column')">
                <div v-if="item.isBestAnswer || item.verifiedPurchase" :class="e('top-row')">
                    <div :class="e('pills-container')">
                        <CpPillPdp
                            v-if="item.isBestAnswer"
                            :class="e('pill')"
                            kind="reviews"
                            variant="mkt"
                            label-kind="white"
                            :label="t('bestAnswer')"
                        />
                        <CpPillPdp
                            v-if="item.verifiedPurchase"
                            :class="e('pill')"
                            kind="reviews"
                            variant="primary"
                            label-kind="white"
                            :label="t('verified')"
                        />
                    </div>
                </div>
                <div :class="e('body')">
                    <CpText variant="body-bold">
                        {{ t('answer') }}
                        <CpTextParser :text="item.body" bold-type="secondary" is-span />
                    </CpText>
                    <CpButtonIcon
                        data-testid="cp-card-qna-answer-button-actions"
                        kind="actions"
                        type="primary"
                        @click="emitEvent('report-answer', item.id); modalReport('answer', item.id)"
                    />
                </div>
                <CpThumbFeedback
                    :class="e('rate')"
                    :kind="'standard'"
                    :like-value="item.positiveVotes"
                    :dislike-value="item.negativeVotes"
                    :user-name="item.userName"
                    :date="item.date"
                    @thumb-up="vote(item.id, '1')"
                    @thumb-down="vote(item.id, '0')"
                />
                <CpDivider v-if="!hasImages && !video && !links" :vertical="false" />
                <div v-if="item.attachments.urls.length > 0" :class="e('right-column')">
                    <CpDivider :vertical="false" />
                    <CpText variant="heading-h3-fake">
                        {{ t('links') }}
                    </CpText>
                    <div :class="e('link-container')">
                        <a v-for="link in item.attachments.urls" :key="link" :href="link">
                            <div :class="e('box')">
                                <CpText :class="e('link')" variant="body-link" type="primary">
                                    {{ link }}
                                </CpText>
                            </div>
                        </a>
                    </div>
                </div>
                <div v-if="item.attachments.video.length > 0">
                    <CpDivider :class="e('section-divider')" :vertical="false" />
                    <CpText variant="heading-h3-fake">
                        {{ t('videos') }}
                    </CpText>
                    <iframe
                        v-for="v in item.attachments.video"
                        :key="v"
                        :class="e('video-box')"
                        width="100%"
                        height="178px"
                        :src="'https://www.youtube.com/embed/' + v"
                        title="YouTube video player"
                        frameborder="0"
                        allow="accelerometer;clipboard-write;encrypted-media;gyroscope;picture-in-picture;web-share"
                        allowfullscreen
                    />
                </div>
                <div v-if="item.attachments.image.length > 0" :class="e('right-column')">
                    <CpDivider :vertical="false" />
                    <CpText variant="heading-h3-fake">
                        {{ t('images') }}
                    </CpText>
                    <CpImage
                        v-for="image in item.attachments.image"
                        :key="image"
                        :class="e('image')"
                        :picture="image"
                        :return-module="false"
                    />
                </div>
            </div>
        </div>

        <CpModalDialog
            v-model:show="showModalRequiredLogin"
            :title="t('warning')"
            :body="t('forYouCanWrite')"
            :label-first-button="t('logIn')"
            type-first-button="primary"
            @first-button="showModalLogin"
        />

        <CpModalReportUcg
            v-if="showModalReportForm"
            v-model:show="showModalReportForm"
            :success="success"
            @update:value="reportReason = $event"
            @send-report="sendReport"
            @on-close="success = false"
        />
        <CpModalActions
            v-model:show="showModalReport"
            with-picture
            type="reportUcg"
            @report-comment="report"
        />
    </div>
</template>

<style scoped lang="scss">
  .cp-card-qna-answer {
    display: flex;
    flex-direction: column;
    min-width: min-content;
    &--full-card {
      border: $cp-border-standard;
      border-radius: $cp-border-radius;
    }
    &__card-header {
      &--no-answers {
        border-bottom: none !important;
      }
    }
    &__main-card {
      padding: $cp-gutter;
      display: flex;
      gap: $cp-gutter;
      border-bottom: $cp-border-standard;
      &--last-card {
        border-bottom: none;
      }
    }
    &__top-row {
      display: flex;
      justify-content: space-between;
      align-items: center;
    }
    &__body {
      display: flex;
      gap: $cp-gutter;
      padding-bottom: $cp-gutter-xsmall;
      justify-content: space-between;
      word-break: break-word;
    }
    &__right-column {
      display: flex;
      flex-direction: column;
      gap: $cp-gutter;
      width: 100%;
    }
    &__bottom-row {
      display: flex;
      justify-content: space-between;
      align-items: center;
    }
    &__info-row {
      display: flex;
      gap: $cp-gutter-xsmall;
    }
    &__like-count {
      display: flex;
      gap: $cp-gutter-xsmall;
      justify-content: center;
      align-items: center;
    }
    &__rate-thumb {
      display: flex;
      gap: $cp-gutter-small;
    }
    &__pills-container {
      display: flex;
      gap: $cp-gutter-small;
    }
    &__box {
      border: 1px solid var(--color-neutral-extra-light);
      border-radius: $cp-border-radius;
      padding: $cp-gutter-small $cp-gutter;
      height: 32px;
      display: grid;
    }
    &__link-container {
      display: flex;
      flex-direction: column;
      gap: $cp-gutter;
    }
    &__video-box {
      border-radius: $cp-border-radius;
      margin-top: $cp-gutter;
    }
    &__link {
      text-decoration: underline;
      color: var(--color-primary);
      display: -webkit-box;
      -webkit-line-clamp: 1;
      -webkit-box-orient: vertical;
      overflow: hidden;
      text-overflow: ellipsis;
    }
    &__section-divider {
      padding-bottom: $cp-gutter;
    }
    &__rate {
      height: 32px;
    }
    &__image {
      height: 158px;
    }
  }
</style>

<i18n locale="es-US" lang="yaml">
  bestAnswer: "Mejor respuesta"
  verified: "Compra verificada"
  answer: "R:"
  links: "Enlaces"
  videos: "Videos de Youtube"
  images: "Imágenes"
  forYouCanWrite: 'Para que puedas realizar un reporte es necesario que inicies sesión.'
  warning: 'Aviso'
  logIn: "Ingresar"
</i18n>
